import axios, { AxiosResponse, AxiosError } from 'axios';
import { getStrapiUrl, getApiUrl, ENDPOINTS } from '../config/api';
import { transformStrapiResponse } from '../utils/strapiTransform';
import {
    CategoryItem,
    CategoryChild,
    SlideshowItem,
    SlideshowSettings,
    ApiResponse,
    ApiError,
    CategoryType,
    MediaFile,
    UploadFormData,
    ContactFormData,
    QuoteFormData,
    EmailApiResponse,
    HelpSection,
    HelpSectionsResponse,
    HelpSectionResponse,
    SocialButton,
    SocialButtonsResponse,
    FooterContent,
    FooterContentResponse,
    Section,
    SectionsResponse,
    SectionResponse,
    PopupSetting
} from '../types';

import { cacheGet, cacheSet } from './cacheManager';
import { getContentVersionCached } from './contentVersion';
import { TEST_DAT } from './test-data';

// Create axios instances
const strapiApi = axios.create({
    baseURL: getStrapiUrl(''),
    headers: {
        'Content-Type': 'application/json',
    },
});

const expressApi = axios.create({
    baseURL: getApiUrl(''),
    headers: {
        'Content-Type': 'application/json',
    },
});

// Cached GET helper for Strapi endpoints
async function getWithCache<T = any>(url: string): Promise<AxiosResponse<T>> {
    // Resolve full URL as axios will join baseURL and url
    const fullUrl = `${strapiApi.defaults.baseURL?.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;

    try {
        const versionInfo = await getContentVersionCached();
        const version = versionInfo?.version ?? -1; // -1 means unknown version; cache entries will be isolated

        // Try cache first
        const entry = await cacheGet<T>(fullUrl);
        if (entry && entry.version === version) {
            return {
                data: entry.data as any,
                status: 200,
                statusText: 'OK (cache)',
                headers: {},
                config: {},
            } as AxiosResponse<T>;
        }

        // Fetch network and update cache
        const response = await strapiApi.get<T>(url);
        // Store in cache in the background (do not block)
        cacheSet({ url: fullUrl, version, savedAt: Date.now(), data: response.data }).catch(() => {});
        return response;
    } catch (e) {
        // On failure (network, version endpoint, etc): fallback to any cache regardless of version
        const entry = await cacheGet<T>(fullUrl);
        if (entry) {
            return {
                data: entry.data as any,
                status: 200,
                statusText: 'OK (stale-cache)',
                headers: {},
                config: {},
            } as AxiosResponse<T>;
        }
        throw e;
    }
}

// Error handler
export const handleApiError = (error: AxiosError<ApiError>): string => {
    if (error.response?.data?.error?.message) {
        return error.response.data.error.message;
    }
    return error.message || 'An unexpected error occurred';
};

// Generic request wrapper with Strapi v4 transformation
async function makeRequest<T>(
    request: () => Promise<AxiosResponse<any>>,
    skipTransform: boolean = false
): Promise<{ data: ApiResponse<T> | null; error: string | null }> {
    try {
        const response = await request();

        // Transform Strapi v4 responses unless explicitly skipped
        if (skipTransform) {
            return { data: response.data as ApiResponse<T>, error: null };
        } else {
            const transformedResponse = transformStrapiResponse<T>(response.data);
            // const transformedResponse = transformStrapiResponse<T>(TEST_DAT);
            return {
                data: {
                    data: transformedResponse.data,
                    meta: transformedResponse.meta
                } as ApiResponse<T>,
                error: null
            };
        }
    } catch (error) {
        return {
            data: null,
            error: handleApiError(error as AxiosError<ApiError>)
        };
    }
}

// Generic request wrapper for non-Strapi APIs (Express APIs)
async function makeRawRequest<T>(
    request: () => Promise<AxiosResponse<T>>
): Promise<{ data: T | null; error: string | null }> {
    try {
        const response = await request();
        return { data: response.data, error: null };
    } catch (error) {
        return {
            data: null,
            error: handleApiError(error as AxiosError<ApiError>)
        };
    }
}// Product API (updated from Category API)
export const productApi = {
    // Get all products with pagination (for infinite scroll)
    getAll: async (page: number = 1, pageSize: number = 25) => {
        return makeRequest<CategoryItem[]>(() =>
            getWithCache(
                `${ENDPOINTS.PRODUCTS}?populate[media]=true&populate[children][populate][media]=true&sort=order_index:asc&pagination[page]=${page}&pagination[pageSize]=${pageSize}`
            )
        );
    },

    // Get ALL products without pagination (for DataProvider/legacy)
    getAllNoPagination: async () => {
        return makeRequest<CategoryItem[]>(() =>
            getWithCache(
                `${ENDPOINTS.PRODUCTS}?populate[media]=true&populate[children][populate][media]=true&sort=order_index:asc&pagination[pageSize]=10000`
            )
        );
    },

    // Get products by type
    getByType: async (type: CategoryType, page: number = 1, pageSize: number = 25) => {
        return makeRequest<CategoryItem[]>(() =>
            getWithCache(
                `${ENDPOINTS.PRODUCTS}?filters[type][$eq]=${type}&populate[media]=true&populate[children][populate][media]=true&sort=order_index:asc&pagination[page]=${page}&pagination[pageSize]=${pageSize}`
            )
        );
    },

    // Create product
    create: async (data: Partial<CategoryItem>) => {
        return makeRequest<CategoryItem>(() =>
            strapiApi.post(ENDPOINTS.PRODUCTS, { data })
        );
    },

    // Update product
    update: async (id: number | string, data: Partial<CategoryItem>) => {
        return makeRequest<CategoryItem>(() =>
            strapiApi.put(`${ENDPOINTS.PRODUCTS}/${id}`, { data })
        );
    },

    // Delete product
    delete: async (id: number | string) => {
        return makeRequest<void>(() =>
            strapiApi.delete(`${ENDPOINTS.PRODUCTS}/${id}`)
        );
    },

    // Upload file for product
    upload: async (formData: FormData) => {
        return makeRequest<MediaFile[]>(() =>
            strapiApi.post(`${ENDPOINTS.UPLOAD}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            })
        );
    },

    // Reorder products (using Express API for complex logic)
    reorder: async (items: Array<{ id: number | string; order_index: number }>) => {
        return makeRequest<any>(() =>
            expressApi.post(`${ENDPOINTS.GALLERY_REORDER}`, { items }),
            true // Skip transform for Express API
        );
    }
};

// Backward compatibility - keep categoryApi as alias to productApi
export const categoryApi = productApi;

// Product Children API (updated from Category Children API)
export const productChildrenApi = {
    // Get children for product
    getByProduct: async (productId: number) => {
        return makeRequest<CategoryChild[]>(() =>
            getWithCache(
                `${ENDPOINTS.PRODUCT_CHILDREN}?filters[product][id][$eq]=${productId}&populate[media]=true&sort=order_index:desc`
            )
        );
    },

    // Create child
    create: async (data: Partial<CategoryChild>) => {
        return makeRequest<CategoryChild>(() =>
            strapiApi.post(ENDPOINTS.PRODUCT_CHILDREN, { data })
        );
    },

    // Update child
    update: async (id: number, data: Partial<CategoryChild>) => {
        return makeRequest<CategoryChild>(() =>
            strapiApi.put(`${ENDPOINTS.PRODUCT_CHILDREN}/${id}`, { data })
        );
    },

    // Delete child
    delete: async (id: number) => {
        return makeRequest<void>(() =>
            strapiApi.delete(`${ENDPOINTS.PRODUCT_CHILDREN}/${id}`)
        );
    }
};

// Backward compatibility - keep categoryChildrenApi as alias to productChildrenApi
export const categoryChildrenApi = {
    ...productChildrenApi,
    // Keep old method name for backward compatibility
    getByCategory: productChildrenApi.getByProduct
};

// Slideshow API
export const slideshowApi = {
    // Get all slideshow items
    getAll: async () => {
        return makeRequest<SlideshowItem[]>(() =>
            getWithCache(
                `${ENDPOINTS.SLIDESHOW_ITEMS}?populate=*&sort=order_index:desc`
            )
        );
    },

    // Create slideshow item
    create: async (data: Partial<SlideshowItem>) => {
        return makeRequest<SlideshowItem>(() =>
            strapiApi.post(ENDPOINTS.SLIDESHOW_ITEMS, { data })
        );
    },

    // Update slideshow item
    update: async (id: number | string, data: Partial<SlideshowItem>) => {
        return makeRequest<SlideshowItem>(() =>
            strapiApi.put(`${ENDPOINTS.SLIDESHOW_ITEMS}/${id}`, { data })
        );
    },

    // Delete slideshow item
    delete: async (id: number | string) => {
        return makeRequest<void>(() =>
            strapiApi.delete(`${ENDPOINTS.SLIDESHOW_ITEMS}/${id}`)
        );
    },

    // Reorder slideshow items
    reorder: async (items: Array<{ id: number | string; order_index: number }>) => {
        return makeRequest<any>(() =>
            expressApi.post('slideshow/reorder', { items }),
            true // Skip transform for Express API
        );
    },

    // Get slideshow settings
    getSettings: async () => {
        return makeRequest<SlideshowSettings>(() =>
            getWithCache(ENDPOINTS.SLIDESHOW_SETTINGS)
        );
    },

    // Update slideshow settings
    updateSettings: async (data: Partial<SlideshowSettings>) => {
        return makeRequest<SlideshowSettings>(() =>
            strapiApi.put(ENDPOINTS.SLIDESHOW_SETTINGS, { data })
        );
    }
};

// Search API
export const searchApi = {
    search: async (query: string) => {
        return makeRawRequest(() =>
            expressApi.get<CategoryItem[]>(`${ENDPOINTS.SEARCH}?q=${encodeURIComponent(query)}`)
        );
    }
};

// Migration API (for V1 to V2 data migration)
export const migrationApi = {
    migrateFromV1: async () => {
        return makeRawRequest(() =>
            expressApi.post<any>(ENDPOINTS.GALLERY_MIGRATE)
        );
    }
};

// Email API
export const emailApi = {
    // Send contact form email
    sendContactForm: async (formData: ContactFormData) => {
        return makeRawRequest(() =>
            expressApi.post<EmailApiResponse>(ENDPOINTS.EMAIL_CONTACT, formData)
        );
    },

    // Send quote request email
    sendQuoteRequest: async (formData: QuoteFormData) => {
        const requestData = {
            ...formData,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            referrer: document.referrer || 'Direct visit',
        };

        return makeRawRequest(() =>
            expressApi.post<EmailApiResponse>(ENDPOINTS.EMAIL_QUOTE, requestData)
        );
    }
};

// Help Sections API
export const helpSectionsApi = {
    // Get all help sections
    getAll: async (locale?: string) => {
        const localeParam = locale ? `&locale=${locale}` : '';
        return makeRequest<HelpSection[]>(() =>
            getWithCache(`${ENDPOINTS.HELP_SECTIONS}?sort=order:asc&populate=localizations&populate=*${localeParam}`)
        );
    },

    // Get help section by ID
    getById: async (id: number, locale?: string) => {
        const localeParam = locale ? `&locale=${locale}` : '';
        return makeRequest<HelpSection>(() =>
            getWithCache(`${ENDPOINTS.HELP_SECTIONS}/${id}?populate=localizations&populate=*${localeParam}`)
        );
    }
};

// Social Buttons API
export const socialButtonsApi = {
    // Get all active social buttons
    getAll: async (locale?: string) => {
        const localeParam = locale ? `&locale=${locale}` : '';
        return makeRequest<SocialButton[]>(() =>
            getWithCache(
                `${ENDPOINTS.SOCIAL_BUTTONS}?filters[isActive][$eq]=true&sort=order:asc&populate=*${localeParam}`
            )
        );
    },

    // Get all social buttons (including inactive for admin)
    getAllAdmin: async (locale?: string) => {
        const localeParam = locale ? `&locale=${locale}` : '';
        return makeRequest<SocialButton[]>(() =>
            getWithCache(`${ENDPOINTS.SOCIAL_BUTTONS}?sort=order:asc&populate=*${localeParam}`)
        );
    },

    // Get social button by ID
    getById: async (id: number, locale?: string) => {
        const localeParam = locale ? `&locale=${locale}` : '';
        return makeRequest<SocialButton>(() =>
            getWithCache(`${ENDPOINTS.SOCIAL_BUTTONS}/${id}?populate=*${localeParam}`)
        );
    }
};

// Footer Content API
export const footerContentApi = {
    // Get footer content (single type)
    get: async () => {
        return makeRequest<FooterContent>(() =>
            getWithCache(`${ENDPOINTS.FOOTER_CONTENT}?populate=*`)
        );
    }
};

// Sections API (for scroll-snap homepage)
export const sectionsApi = {
    // Get all sections with children
    getAll: async (locale?: string) => {
        const localeParam = locale ? `&locale=${locale}` : '';
        return makeRequest<Section[]>(() =>
            getWithCache(
                `${ENDPOINTS.SECTIONS}?` +
                `populate[slide][populate][media]=*&` +
                `populate[slide][populate][left_media]=*&` +
                `populate[slide][populate][right_media]=*&` +
                `populate[slide][populate][content_media]=*&` +
                `populate[child_slides][populate][slide_data][populate][media]=*&` +
                `populate[child_slides][populate][slide_data][populate][left_media]=*&` +
                `populate[child_slides][populate][slide_data][populate][right_media]=*&` +
                `populate[child_slides][populate][slide_data][populate][content_media]=*&` +
                `populate[child_slides][sort][0]=order_index:asc&` +
                `populate=localizations&` +
                `sort=order_index:asc${localeParam}`
            )
        );
    },

    // Get section by ID
    getById: async (id: number | string, locale?: string) => {
        const localeParam = locale ? `&locale=${locale}` : '';
        return makeRequest<Section>(() =>
            getWithCache(
                `${ENDPOINTS.SECTIONS}/${id}?` +
                `populate[slide][populate][media]=*&` +
                `populate[slide][populate][left_media]=*&` +
                `populate[slide][populate][right_media]=*&` +
                `populate[slide][populate][content_media]=*&` +
                `populate[child_slides][populate][slide_data][populate][media]=*&` +
                `populate[child_slides][populate][slide_data][populate][left_media]=*&` +
                `populate[child_slides][populate][slide_data][populate][right_media]=*&` +
                `populate[child_slides][populate][slide_data][populate][content_media]=*&` +
                `populate=localizations${localeParam}`
            )
        );
    },

    // Create section
    create: async (data: Partial<Section>) => {
        return makeRequest<Section>(() =>
            strapiApi.post(ENDPOINTS.SECTIONS, { data })
        );
    },

    // Update section
    update: async (id: number | string, data: Partial<Section>) => {
        return makeRequest<Section>(() =>
            strapiApi.put(`${ENDPOINTS.SECTIONS}/${id}`, { data })
        );
    },

    // Delete section
    delete: async (id: number | string) => {
        return makeRequest<void>(() =>
            strapiApi.delete(`${ENDPOINTS.SECTIONS}/${id}`)
        );
    }
};

// Popup Settings API
export const popupSettingsApi = {
    get: async (locale?: string) => {
        const localeParam = locale ? `?locale=${locale}` : '';
        return makeRequest<PopupSetting>(() =>
            getWithCache(`${ENDPOINTS.POPUP_SETTINGS}${localeParam}`)
        );
    }
};